import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Form, Spinner, Alert, Badge, ProgressBar } from 'react-bootstrap';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import './providerForm.css';


const API_URL = process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec';

const DocumentUpload = ({ nextStep, isReadOnly = false }) => {
  const [preguntas, setPreguntas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [deleting, setDeleting] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [idFormulario, setIdFormulario] = useState(null);
  const [formEstado, setFormEstado] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showAdicional, setShowAdicional] = useState(false);
  const [adicionalTitulo, setAdicionalTitulo] = useState("");
  const [adicionalDescripcion, setAdicionalDescripcion] = useState("");
  const [adicionalArchivo, setAdicionalArchivo] = useState(null);
  const [uploadingAdicional, setUploadingAdicional] = useState(false);
  const [razonSocial, setRazonSocial] = useState("");
  const [snack, setSnack] = useState({ open: false, text: '', severity: 'success' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [showDeleteAdicionalConfirm, setShowDeleteAdicionalConfirm] = useState(false);
  const [deleteAdicionalId, setDeleteAdicionalId] = useState(null);
  const [deleteAdicionalLoading, setDeleteAdicionalLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const idFormularioFromURL = searchParams.get('id_formulario');

  const showSnackbar = (text, severity = 'success') => setSnack({ open: true, text, severity });
  const closeSnack = () => setSnack({ ...snack, open: false });

  const obtenerRazonSocial = async (formularioId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/providers/proveedor-datos/${formularioId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      if (response.data && response.data.razon_social) {
        setRazonSocial(response.data.razon_social);
        return response.data.razon_social;
      }
      return "";
    } catch (error) {
      console.error('Error obteniendo razón social:', error);
      return "";
    }
  };

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const formulariosRes = await axios.get(`${API_URL}/api/formularios`, {
        headers,
        withCredentials: true,
        timeout: 10000
      });

      const formulario = idFormularioFromURL
        ? formulariosRes.data.find(f => f.id_formulario === parseInt(idFormularioFromURL))
        : formulariosRes.data.find(f => f.estado === 'activo');

      if (!formulario) {
        showSnackbar('No se encontró un formulario válido.', 'error');
        setLoading(false);
        return;
      }

      setIdFormulario(formulario.id_formulario);
      setFormEstado(formulario.estado);

      const razonSocialActual = await obtenerRazonSocial(formulario.id_formulario);
      setRazonSocial(razonSocialActual);

      const [respuestasRes, adicionalesRes, preguntasRes] = await Promise.all([
        axios.get(`${API_URL}/api/documentos/respuestas?id_formulario=${formulario.id_formulario}`, {
          headers,
          withCredentials: true,
          timeout: 10000
        }).catch(err => ({ data: [] })),
        axios.get(`${API_URL}/api/documentos/certificados-adicionales?id_formulario=${formulario.id_formulario}`, {
          headers,
          withCredentials: true,
          timeout: 10000
        }).catch(err => ({ data: [] })),
        formulario.id_formulario_general ?
          axios.get(`${API_URL}/api/formularios-generales/${formulario.id_formulario_general}/preguntas-filtradas`, {
            headers,
            withCredentials: true,
            timeout: 10000
          }).catch(err => ({ data: [] })) : { data: [] }
      ]);

      const respuestas = respuestasRes.data || [];
      const adicionales = adicionalesRes.data || [];
      const preguntasGenerales = preguntasRes.data || [];

      const adicionalesProcesados = adicionales.map((a) => ({
        id_pregunta: `adic-${a.id_certificado}`,
        titulo: a.titulo,
        descripcion: a.descripcion,
        tipo: 'documento',
        obligatorio: true,
        archivo: null,
        subido: !!a.ruta_archivo,
        nombre_archivo: a.ruta_archivo ? a.ruta_archivo.split('/').pop() : null,
        estado: a.estado || 'pendiente',
        ruta_archivo: a.ruta_archivo,
        id_respuesta: a.id_certificado,
        esAdicional: true,
        id_certificado: a.id_certificado,
      }));

      const normalesProcesadas = preguntasGenerales
        .filter((p) => p.tipo === 'documento' && p.tipo_persona === type)
        .map((pregunta) => {
          const respuesta = respuestas.find((r) => r.id_pregunta === pregunta.id_pregunta);
          return {
            id_pregunta: `gen-${pregunta.id_pregunta}`,
            titulo: pregunta.titulo,
            descripcion: pregunta.descripcion,
            tipo: pregunta.tipo,
            tipo_persona: pregunta.tipo_persona,
            obligatorio: pregunta.obligatorio,
            archivo: null,
            subido: !!respuesta?.ruta_archivo,
            nombre_archivo: respuesta?.ruta_archivo ? respuesta.ruta_archivo.split('/').pop() : null,
            respuesta_texto: respuesta?.respuesta_texto || null,
            estado: respuesta?.estado || 'pendiente',
            ruta_archivo: respuesta?.ruta_archivo || null,
            id_respuesta: respuesta?.id_respuesta || null,
            esAdicional: false,
            id_pregunta_real: pregunta.id_pregunta,
          };
        });

      setPreguntas([...normalesProcesadas, ...adicionalesProcesados]);

    } catch (err) {
      console.error('Error al cargar preguntas:', err);
      showSnackbar('No se pudieron cargar los documentos requeridos', 'error');
    } finally {
      setLoading(false);
    }
  }, [idFormularioFromURL, type]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleFileChange = async (id, e, esAdicional = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('pdf')) {
      showSnackbar('Solo se permiten archivos PDF.', 'error');
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('El archivo es demasiado grande. Máximo 5MB permitido.', 'error');
      e.target.value = "";
      return;
    }

    setUploading(prev => ({ ...prev, [id]: true }));
    setUploadProgress(prev => ({ ...prev, [id]: 0 }));

    const formData = new FormData();
    formData.append("archivo", file);
    formData.append("id_formulario", idFormulario);

    if (esAdicional) {
      const pregunta = preguntas.find(p => p.id_pregunta === id);
      formData.append("titulo", pregunta.titulo);
      formData.append("descripcion", pregunta.descripcion);
    } else {
      const pregunta = preguntas.find(p => p.id_pregunta === id);
      formData.append("id_pregunta", pregunta.id_pregunta_real);
    }

    try {
      const token = localStorage.getItem('token');
      let endpoint;
      let res;

      if (esAdicional) {
        endpoint = "/certificados-adicionales";
        res = await axios.post(`${API_URL}/api/documentos${endpoint}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
          timeout: 60000,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [id]: percentCompleted }));
          }
        });
      } else {
        endpoint = "/respuestas/upload";
        res = await axios.post(`${API_URL}/api/documentos${endpoint}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
          timeout: 60000,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [id]: percentCompleted }));
          }
        });
      }

      setPreguntas(prev =>
        prev.map(p => {
          if (p.id_pregunta === id) {
            const responseData = res.data.data || res.data;
            const nombreArchivo = file.name;
            return {
              ...p,
              subido: true,
              nombre_archivo: nombreArchivo,
              ruta_archivo: responseData.ruta_archivo || responseData.nombre_archivo || nombreArchivo,
              estado: "pendiente",
              id_respuesta: esAdicional
                ? responseData.id_certificado
                : responseData.id_respuesta,
              id_certificado: esAdicional ? responseData.id_certificado : p.id_certificado
            };
          }
          return p;
        })
      );

      setUploadProgress(prev => ({ ...prev, [id]: 100 }));

      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[id];
          return newProgress;
        });
      }, 2000);

      showSnackbar('Archivo subido correctamente', 'success');

    } catch (err) {
      console.error("Error al subir archivo:", err);
      showSnackbar('Error al subir archivo. Por favor, intente nuevamente.', 'error');
    } finally {
      e.target.value = "";
      setUploading(prev => ({ ...prev, [id]: false }));
    }
  };

  const openDeleteModal = (id) => {
    if (isReadOnly) return;
    const pregunta = preguntas.find(p => p.id_pregunta === id);
    if (!pregunta || !pregunta.id_respuesta) return;
    setDeleteInfo({ id, pregunta });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteInfo) return;

    const { id, pregunta } = deleteInfo;
    setDeleting(prev => ({ ...prev, [id]: true }));

    try {
      const token = localStorage.getItem('token');
      let endpoint;

      if (pregunta.esAdicional) {
        endpoint = `/certificados-adicionales/${pregunta.id_certificado}`;
      } else {
        endpoint = `/respuestas/${pregunta.id_respuesta}`;
      }

      setPreguntas(prev =>
        prev.map(p =>
          p.id_pregunta === id
            ? {
              ...p,
              subido: false,
              nombre_archivo: null,
              ruta_archivo: null,
              estado: 'pendiente',
              id_respuesta: null,
            }
            : p
        )
      );

      const response = await axios.delete(`${API_URL}/api/documentos${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        timeout: 15000
      });

      if (response.status === 200 || response.status === 204) {
        showSnackbar('Archivo eliminado correctamente', 'warning');
        setShowDeleteConfirm(false);
        setDeleteInfo(null);
      } else if (response.status === 404) {
        showSnackbar('El archivo ya no existe en el sistema', 'info');
      }

    } catch (err) {
      console.error('Error al eliminar archivo:', err);

      if (err.response?.status === 404) {
        showSnackbar('El archivo ya no existe, refrescando datos...', 'info');
      } else {
        showSnackbar('Error al eliminar el archivo. Por favor, intente nuevamente.', 'error');
      }

      await cargarDatos();
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  const openDeleteAdicionalModal = (idCertificado) => {
    setDeleteAdicionalId(idCertificado);
    setShowDeleteAdicionalConfirm(true);
  };

  const confirmDeleteAdicional = async () => {
    if (!deleteAdicionalId) return;

    setDeleteAdicionalLoading(true);

    try {
      const token = localStorage.getItem('token');

      setPreguntas(prev =>
        prev.filter(p => !(p.esAdicional && p.id_certificado === deleteAdicionalId))
      );

      const response = await fetch(`${API_URL}/api/documentos/certificados-adicionales/${deleteAdicionalId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        showSnackbar(
          data.message || 'Certificado adicional eliminado correctamente',
          'warning'
        );
        setShowDeleteAdicionalConfirm(false);
        setDeleteAdicionalId(null);
      } else if (response.status === 404) {
        showSnackbar('Certificado adicional eliminado correctamente', 'info');
        await cargarDatos();
        setShowDeleteAdicionalConfirm(false);
        setDeleteAdicionalId(null);
      } else {
        const error = await response.json();
        showSnackbar(`Error: ${error.detail || 'No se pudo eliminar'}`, 'error');
        await cargarDatos();
      }
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Error al eliminar el certificado', 'error');
      await cargarDatos();
    } finally {
      setDeleteAdicionalLoading(false);
    }
  };

  const getFileUrl = (rutaArchivo) => {
    if (!rutaArchivo) return "#";

    const rutaCodificada = encodeURIComponent(rutaArchivo);
    return `${API_URL}/api/documentos/visualizar/${rutaCodificada}`;
  };

  const shortenFileName = (fileName, maxLength = 20) => {
    if (!fileName) return 'Seleccionar archivo';
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.length - extension.length - 1);
    const shortenedName = nameWithoutExt.substring(0, maxLength - extension.length - 3) + '...';
    return `${shortenedName}.${extension}`;
  };

  const handleGuardarAdicional = async () => {
    if (!adicionalArchivo || !adicionalTitulo.trim()) {
      showSnackbar('Título y archivo son requeridos', 'error');
      return;
    }

    if (!adicionalArchivo.type.includes('pdf')) {
      showSnackbar('Solo se permiten archivos PDF.', 'error');
      return;
    }

    if (adicionalArchivo.size > 5 * 1024 * 1024) {
      showSnackbar('El archivo es demasiado grande. Máximo 5MB permitido.', 'error');
      return;
    }

    setUploadingAdicional(true);

    const descripcionFinal = adicionalDescripcion.trim() || "Sin descripción";

    const formData = new FormData();
    formData.append("titulo", adicionalTitulo.trim());
    formData.append("descripcion", descripcionFinal);
    formData.append("archivo", adicionalArchivo);
    formData.append("id_formulario", idFormulario);

    const token = localStorage.getItem('token');

    try {
      const res = await axios.post(
        `${API_URL}/api/documentos/certificados-adicionales`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
          timeout: 30000
        }
      );

      const nuevoCertificado = {
        id_pregunta: `adic-${res.data.data.id_certificado}`,
        titulo: adicionalTitulo.trim(),
        descripcion: descripcionFinal,
        tipo: 'documento',
        obligatorio: true,
        archivo: null,
        subido: true,
        nombre_archivo: adicionalArchivo.name,
        estado: 'pendiente',
        ruta_archivo: res.data.data.ruta_archivo,
        id_respuesta: res.data.data.id_certificado,
        esAdicional: true,
        id_certificado: res.data.data.id_certificado,
      };

      setPreguntas(prev => [...prev, nuevoCertificado]);

      showSnackbar('Certificado adicional guardado correctamente', 'success');
      setShowAdicional(false);
      setAdicionalTitulo('');
      setAdicionalDescripcion('');
      setAdicionalArchivo(null);

    } catch (err) {
      console.error("Error al guardar certificado adicional:", err);
      showSnackbar('Error al guardar certificado adicional', 'error');
    } finally {
      setUploadingAdicional(false);
    }
  };

  const handleAnterior = () => {
    const newRoute = `/form/${type}/1${idFormulario ? `?id_formulario=${idFormulario}` : ''}`;
    navigate(newRoute);
  };

  const handleSiguiente = async (e) => {
    e.preventDefault();

    if (!allRequiredAnswered) {
      showSnackbar("Debes subir todos los documentos obligatorios antes de continuar.", "error");
      return;
    }

    await nextStep();
  };

  const allRequiredAnswered = preguntas
    .filter(p => p.obligatorio && !p.esAdicional)
    .every(p => p.subido);

  if (loading && preguntas.length === 0) {
    return (
      <div className="py-4 text-center">
        <Spinner animation="border" variant="primary">
          <span className="visually-hidden">Cargando documentos...</span>
        </Spinner>
        <p className="mt-2">Cargando documentos requeridos...</p>
      </div>
    );
  }

  return (
    <div className="form-section">
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={closeSnack}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.text}
        </MuiAlert>
      </Snackbar>

      <h2 className="mb-4">Documentos Requeridos</h2>
      <p className="mb-4 text-muted">
        {isReadOnly
          ? 'Estos son los archivos subidos:'
          : 'Por favor complete la información requerida:'}
      </p>

      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible className="mb-4">
          {successMessage}
        </Alert>
      )}

      {!isReadOnly && (
        <div className="mb-4">
          <Button
            variant="outline-primary"
            onClick={() => setShowAdicional(true)}
            disabled={uploadingAdicional}
          >
            Agregar certificado adicional
          </Button>
        </div>
      )}

      {showAdicional && (
        <Card className="mb-4">
          <Card.Header style={{ color: '#004892' }}>
            <strong>Agregar Certificado Adicional</strong>
            <p>En este apartado se deberán cargar todos los certificados adicionales a los solicitados.</p>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Título</Form.Label>
              <Form.Control
                type="text"
                value={adicionalTitulo}
                onChange={(e) => setAdicionalTitulo(e.target.value.substring(0, 100))}
                placeholder="Título del certificado"
                disabled={uploadingAdicional}
                maxLength="100"
              />
              <Form.Text className="text-muted">
                {adicionalTitulo.length}/100 caracteres
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={adicionalDescripcion}
                onChange={(e) => setAdicionalDescripcion(e.target.value.substring(0, 200))}
                placeholder="Breve descripción del certificado"
                disabled={uploadingAdicional}
                maxLength="200"
              />
              <Form.Text className="text-muted">
                {adicionalDescripcion.length}/200 caracteres
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Archivo</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                onChange={(e) => setAdicionalArchivo(e.target.files[0])}
                disabled={uploadingAdicional}
              />
              <Form.Text className="text-muted">
                Solo archivos PDF. Máximo 5MB.
              </Form.Text>
            </Form.Group>
            <div className="d-flex gap-2">
              <Button
                variant="success"
                onClick={handleGuardarAdicional}
                disabled={!adicionalTitulo.trim() || !adicionalArchivo || uploadingAdicional}
              >
                {uploadingAdicional ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Guardando...
                  </>
                ) : (
                  'Guardar'
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowAdicional(false)}
                disabled={uploadingAdicional}
              >
                Cancelar
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <Form onSubmit={handleSiguiente}>
        {preguntas.map((pregunta) => {
          const inputId = `file-${pregunta.id_pregunta}`;
          const isUploading = uploading[pregunta.id_pregunta];
          const isDeleting = deleting[pregunta.id_pregunta];
          const progress = uploadProgress[pregunta.id_pregunta];

          return (
            <Card key={pregunta.id_pregunta} className="shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-start mb-3">
                  <Card.Title className="h5 mb-0">{pregunta.titulo}</Card.Title>
                  <div className="d-flex align-items-center gap-2">
                    {pregunta.obligatorio && !pregunta.esAdicional ? (
                      <Badge bg="danger">Obligatorio</Badge>
                    ) : pregunta.esAdicional ? (
                      <Badge bg="info">Adicional</Badge>
                    ) : (
                      <Badge bg="secondary">Opcional</Badge>
                    )}
                    {pregunta.esAdicional && !isReadOnly && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openDeleteAdicionalModal(pregunta.id_certificado)}
                        disabled={isDeleting}
                        title="Eliminar certificado adicional"
                      >
                        Eliminar
                      </Button>
                    )}
                  </div>
                </div>

                {pregunta.descripcion && (
                  <Card.Text className="text-muted mb-3 text-wrap">
                    {pregunta.descripcion}
                  </Card.Text>
                )}

                <div className="d-flex align-items-center mb-2">
                  {pregunta.subido ? (
                    <div className="d-flex flex-column w-100">
                      <div className="d-flex align-items-center mb-2">
                        <a
                          href={getFileUrl(pregunta.ruta_archivo)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="me-3 text-truncate text-decoration-none fw-bold"
                          style={{ maxWidth: '250px' }}
                          title={pregunta.nombre_archivo}
                        >
                          {shortenFileName(pregunta.nombre_archivo)}
                        </a>
                        {!isReadOnly && !pregunta.esAdicional && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => openDeleteModal(pregunta.id_pregunta)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              'Eliminar'
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="w-100">
                      {formEstado === 'finalizado' ? (
                        <div className="text-danger fw-bold">Sin archivos</div>
                      ) : (
                        <>
                          <div className="input-group">
                            <input
                              type="file"
                              id={inputId}
                              className="form-control d-none"
                              accept=".pdf"
                              onChange={(e) => handleFileChange(pregunta.id_pregunta, e, pregunta.esAdicional)}
                              disabled={isUploading || isReadOnly}
                            />
                            <label htmlFor={inputId} className="btn btn-outline-primary flex-grow-1">
                              {isUploading ? (
                                <>
                                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                                  <span>Subiendo... {progress}%</span>
                                </>
                              ) : (
                                'Seleccionar archivo PDF'
                              )}
                            </label>
                          </div>
                          {isUploading && progress > 0 && (
                            <ProgressBar
                              now={progress}
                              className="mt-2"
                              variant="success"
                              animated
                              label={`${progress}%`}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <Form.Text className="text-muted">
                  Formatos aceptados: PDF (Máx. 5MB)
                </Form.Text>
              </Card.Body>
            </Card>
          );
        })}

        {preguntas.filter(p => !p.esAdicional).length === 0 && !loading && (
          <Card className="text-center py-5">
            <Card.Body>
              <h5 className="text-muted">No hay documentos requeridos</h5>
              <p className="text-muted">No se han configurados documentos para este formulario.</p>
            </Card.Body>
          </Card>
        )}

        <div className="d-flex justify-content-between mt-4">
          <Button
            variant="outline-secondary"
            onClick={handleAnterior}
            disabled={loading}
          >
            Anterior
          </Button>

          <Button
            variant="primary"
            type="submit"
            disabled={!allRequiredAnswered || loading}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Guardando...
              </>
            ) : (
              'Siguiente'
            )}
          </Button>
        </div>
      </Form>

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setDeleteInfo(null);
        }}
        onConfirm={confirmDelete}
        title="¿Está seguro de que desea eliminar este archivo?"
        message="Esta acción eliminará el archivo de forma permanente. No se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmVariant="danger"
        loading={deleteInfo && deleting[deleteInfo.id]}
        icon={<i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>}
      />

      <ConfirmModal
        show={showDeleteAdicionalConfirm}
        onHide={() => {
          setShowDeleteAdicionalConfirm(false);
          setDeleteAdicionalId(null);
        }}
        onConfirm={confirmDeleteAdicional}
        title="¿Está seguro de eliminar este certificado adicional?"
        message="Esta acción eliminará el certificado adicional de forma permanente."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmVariant="danger"
        loading={deleteAdicionalLoading}
        icon={<i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>}
      />
    </div>
  );
};

export default DocumentUpload;